import React, { useState } from "react";
import {
  AdminAccessRequest,
  createAccessRequest,
} from "../../api/admin";
import { listEvents } from "../../api/events";
import ErrorAlert from "../ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";

interface Props {
  requests: AdminAccessRequest[];
  onRequestSubmitted: () => void;
  compact?: boolean;
}

export default function RequestEventAdminAccess({
  requests,
  onRequestSubmitted,
  compact = false,
}: Props) {
  const [showModal, setShowModal] = useState(false);
  const [publishedEvents, setPublishedEvents] = useState<
    { id: number; title: string }[]
  >([]);
  const [selectedEventId, setSelectedEventId] = useState<number | "">("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openModal = async () => {
    setShowModal(true);
    setError(null);
    try {
      const all = await listEvents({ status: "published", limit: 100 });
      setPublishedEvents(all.map((e) => ({ id: e.id, title: e.title })));
    } catch {
      setPublishedEvents([]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedEventId) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await createAccessRequest(Number(selectedEventId), message || undefined);
      setShowModal(false);
      setSelectedEventId("");
      setMessage("");
      onRequestSubmitted();
    } catch (err) {
      setError(
        getUserFacingErrorMessage(err, "Could not submit request."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={compact ? "eh-btn-primary text-sm" : "eh-btn-primary"}
        onClick={openModal}
      >
        Request event admin access
      </button>

      {requests.length > 0 && (
        <div className={`space-y-3 ${compact ? "mt-4" : "mt-6"}`}>
          {requests.map((req) => (
            <div
              key={req.id}
              className="border border-slate-200 p-4 rounded-sm bg-slate-50 text-sm"
            >
              <p className="font-medium text-slate-900">
                {req.event?.title ?? "Event admin access"}
              </p>
              <p className="text-slate-600 mt-1">
                Status:{" "}
                <span
                  className={
                    req.status === "approved"
                      ? "text-green-700 font-medium"
                      : req.status === "rejected"
                        ? "text-red-700 font-medium"
                        : "text-amber-700 font-medium"
                  }
                >
                  {req.status}
                </span>
                {" · "}
                Submitted {req.createdAt.slice(0, 10)}
              </p>
              {req.message && (
                <p className="mt-2 text-slate-700">{req.message}</p>
              )}
              {req.reviewNote && (
                <p className="mt-1 text-slate-500">Note: {req.reviewNote}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-sm max-w-md w-full shadow-lg">
            <h3 className="font-bold text-lg mb-4">Request event admin access</h3>
            {error && <ErrorAlert className="mb-3">{error}</ErrorAlert>}
            <label className="block text-sm mb-1">Event</label>
            <select
              className="w-full border border-slate-300 p-2 mb-4 text-sm"
              value={selectedEventId}
              onChange={(e) =>
                setSelectedEventId(e.target.value ? Number(e.target.value) : "")
              }
            >
              <option value="">Select an event…</option>
              {publishedEvents.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}
                </option>
              ))}
            </select>
            <label className="block text-sm mb-1">Message (optional)</label>
            <textarea
              className="w-full border border-slate-300 p-2 mb-4 text-sm"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Why do you need access to this event?"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="eh-btn-secondary text-sm"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="eh-btn-primary text-sm"
                disabled={!selectedEventId || isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
